/**
 * Query keys for admin (React Query).
 *
 * Invalidation: use helpers in `adminQueryInvalidation.ts` so list + detail stay consistent
 * (e.g. note save, future deletion).
 */

export interface AdminUsersListParams {
  page: number;
  limit: number;
  search: string;
  filtersKey: string;
  sortField: string;
  sortDirection: string;
}

/** Params for cursor-based infinite list (GET /admin/users). */
export interface AdminUsersInfiniteListParams {
  limit: number;
  search: string;
  filtersKey: string;
  sortField: string;
  sortDirection: string;
}

export const adminUsersQueryKeys = {
  all: ['admin-users'] as const,
  lists: () => [...adminUsersQueryKeys.all, 'list'] as const,
  list: (params: AdminUsersListParams) => [...adminUsersQueryKeys.lists(), params] as const,
  infiniteLists: () => [...adminUsersQueryKeys.all, 'infinite-list'] as const,
  infiniteList: (params: AdminUsersInfiniteListParams) =>
    [...adminUsersQueryKeys.infiniteLists(), params] as const,
};

/** React Query keys for admin user detail (profile + lazy tabs). */
export const adminUserDetailQueryKeys = {
  all: ['admin-user-detail'] as const,
  detail: (userId: string) => [...adminUserDetailQueryKeys.all, userId] as const,
  businesses: (userId: string) =>
    [...adminUserDetailQueryKeys.detail(userId), 'businesses'] as const,
  notes: (userId: string) => [...adminUserDetailQueryKeys.detail(userId), 'notes'] as const,
  reports: (userId: string, direction: string) =>
    [...adminUserDetailQueryKeys.detail(userId), 'reports', direction] as const,
  communities: (userId: string) =>
    [...adminUserDetailQueryKeys.detail(userId), 'communities'] as const,
};

/** React Query keys for admin deletion preview, resolution, and job status. */
export const adminDeletionQueryKeys = {
  all: ['admin-deletion'] as const,
  previews: () => [...adminDeletionQueryKeys.all, 'preview'] as const,
  preview: (targetType: string, targetId: string) =>
    [...adminDeletionQueryKeys.previews(), targetType, targetId] as const,
  /** Per-hook scope while preview is disabled (avoids preview(type, '') cache collisions). */
  previewDisabled: (scopeId: string) =>
    [...adminDeletionQueryKeys.previews(), 'disabled', scopeId] as const,
  businessResolutions: () => [...adminDeletionQueryKeys.all, 'business-resolution'] as const,
  businessResolution: (userId: string) =>
    [...adminDeletionQueryKeys.businessResolutions(), userId] as const,
  businessResolutionDisabled: (scopeId: string) =>
    [...adminDeletionQueryKeys.businessResolutions(), 'disabled', scopeId] as const,
  jobs: () => [...adminDeletionQueryKeys.all, 'jobs'] as const,
  job: (jobId: string) => [...adminDeletionQueryKeys.jobs(), jobId] as const,
  jobDisabled: (scopeId: string) =>
    [...adminDeletionQueryKeys.jobs(), 'disabled', scopeId] as const,
  jobsByTarget: (targetType: string, targetId: string) =>
    [...adminDeletionQueryKeys.jobs(), 'by-target', targetType, targetId] as const,
};

/** QA cleanup tooling React Query keys. */
export const devToolsQueryKeys = {
  status: () => ['dev-tools', 'status'] as const,
};

export const qaDeletionQueryKeys = {
  access: (userEmail: string) => ['qa-deletion-access', userEmail] as const,
  usersAll: () => ['qa-deletion-users'] as const,
  users: (search: string) => [...qaDeletionQueryKeys.usersAll(), search] as const,
};

export interface AdminTemplatesListParams {
  cursor?: string;
  limit: number;
  type?: string;
  status?: string;
}

export const adminTemplatesQueryKeys = {
  all: ['admin-templates'] as const,
  lists: () => [...adminTemplatesQueryKeys.all, 'list'] as const,
  list: (params: AdminTemplatesListParams) => [...adminTemplatesQueryKeys.lists(), params] as const,
};

export const adminAutomationTemplatesQueryKeys = {
  all: ['admin-automation-templates'] as const,
  lists: () => [...adminAutomationTemplatesQueryKeys.all, 'list'] as const,
  list: (params: AdminAutomationTemplatesListParams) =>
    [...adminAutomationTemplatesQueryKeys.lists(), params] as const,
  detail: (id: string) => [...adminAutomationTemplatesQueryKeys.all, 'detail', id] as const,
};

export interface AdminAutomationTemplatesListParams {
  cursor?: string;
  limit: number;
  status?: string;
  category?: string;
  search?: string;
}

export const adminPlansQueryKeys = {
  all: ['admin-plans'] as const,
  lists: () => [...adminPlansQueryKeys.all, 'list'] as const,
  list: () => [...adminPlansQueryKeys.lists()] as const,
};

export const adminJobPayDisputesQueryKeys = {
  all: ['admin-job-pay-disputes'] as const,
  lists: () => [...adminJobPayDisputesQueryKeys.all, 'list'] as const,
  list: (cursor: string | null) => [...adminJobPayDisputesQueryKeys.lists(), cursor] as const,
};

export const adminJobPayFeesQueryKeys = {
  all: ['admin-job-pay-fees'] as const,
  settings: () => [...adminJobPayFeesQueryKeys.all, 'settings'] as const,
  overrides: (cursor: string | null) =>
    [...adminJobPayFeesQueryKeys.all, 'overrides', cursor] as const,
};

export const adminJobPayMoneyQueryKeys = {
  all: ['admin-job-pay-money'] as const,
  summary: () => [...adminJobPayMoneyQueryKeys.all, 'summary'] as const,
  payments: (state: string) => [...adminJobPayMoneyQueryKeys.all, 'payments', state] as const,
  reconcile: () => [...adminJobPayMoneyQueryKeys.all, 'reconcile'] as const,
  timeline: (jobPaymentId: string) =>
    [...adminJobPayMoneyQueryKeys.all, 'timeline', jobPaymentId] as const,
  recoveries: () => [...adminJobPayMoneyQueryKeys.all, 'recoveries'] as const,
  strikes: () => [...adminJobPayMoneyQueryKeys.all, 'strikes'] as const,
  audit: (jobPaymentId: string, action: string) =>
    [...adminJobPayMoneyQueryKeys.all, 'audit', jobPaymentId, action] as const,
};
